import { useState } from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Bold,
  Italic,
  Code,
  Link as LinkIcon,
  List,
  ListOrdered,
  Quote,
  Heading2,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  minRows?: number;
}

export const MarkdownEditor = ({
  value,
  onChange,
  placeholder = 'Schreibe einen Kommentar... (Markdown wird unterstützt)',
  disabled = false,
  minRows = 4,
}: MarkdownEditorProps) => {
  const [activeTab, setActiveTab] = useState<'write' | 'preview'>('write');

  const insertMarkdown = (before: string, after: string = '') => {
    const textarea = document.querySelector('textarea') as HTMLTextAreaElement;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);
    const newValue =
      value.substring(0, start) +
      before +
      selectedText +
      after +
      value.substring(end);

    onChange(newValue);

    // Set cursor position after insertion
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + before.length + selectedText.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const toolbarButtons = [
    {
      icon: Bold,
      label: 'Fett',
      action: () => insertMarkdown('**', '**'),
    },
    {
      icon: Italic,
      label: 'Kursiv',
      action: () => insertMarkdown('*', '*'),
    },
    {
      icon: Heading2,
      label: 'Überschrift',
      action: () => insertMarkdown('## ', ''),
    },
    {
      icon: Code,
      label: 'Code',
      action: () => insertMarkdown('`', '`'),
    },
    {
      icon: LinkIcon,
      label: 'Link',
      action: () => insertMarkdown('[', '](url)'),
    },
    {
      icon: List,
      label: 'Liste',
      action: () => insertMarkdown('- ', ''),
    },
    {
      icon: ListOrdered,
      label: 'Nummerierte Liste',
      action: () => insertMarkdown('1. ', ''),
    },
    {
      icon: Quote,
      label: 'Zitat',
      action: () => insertMarkdown('> ', ''),
    },
  ];

  return (
    <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'write' | 'preview')}>
      <div className="border border-border rounded-lg overflow-hidden">
        <div className="flex items-center justify-between bg-muted/30 px-3 py-2 border-b border-border">
          <div className="flex items-center gap-1">
            {toolbarButtons.map((button) => (
              <Button
                key={button.label}
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={button.action}
                disabled={disabled || activeTab === 'preview'}
                title={button.label}
              >
                <button.icon className="h-4 w-4" />
              </Button>
            ))}
          </div>
          <TabsList className="h-8">
            <TabsTrigger value="write" className="text-xs">
              Schreiben
            </TabsTrigger>
            <TabsTrigger value="preview" className="text-xs">
              Vorschau
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="write" className="m-0">
          <TextareaAutosize
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            disabled={disabled}
            minRows={minRows}
            className={cn(
              'w-full px-3 py-2 bg-background text-foreground resize-none focus:outline-none',
              'placeholder:text-muted-foreground'
            )}
          />
        </TabsContent>

        <TabsContent value="preview" className="m-0">
          <div className="px-3 py-2 min-h-[100px] prose prose-sm dark:prose-invert max-w-none">
            {value ? (
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeSanitize]}
                components={{
                  // Custom styling for markdown elements
                  h2: ({ node, ...props }) => (
                    <h2 className="text-lg font-semibold mt-4 mb-2" {...props} />
                  ),
                  h3: ({ node, ...props }) => (
                    <h3 className="text-base font-semibold mt-3 mb-2" {...props} />
                  ),
                  p: ({ node, ...props }) => (
                    <p className="mb-2 leading-relaxed" {...props} />
                  ),
                  ul: ({ node, ...props }) => (
                    <ul className="list-disc list-inside mb-2 space-y-1" {...props} />
                  ),
                  ol: ({ node, ...props }) => (
                    <ol className="list-decimal list-inside mb-2 space-y-1" {...props} />
                  ),
                  code: ({ node, inline, ...props }: any) =>
                    inline ? (
                      <code
                        className="bg-muted px-1 py-0.5 rounded text-sm font-mono"
                        {...props}
                      />
                    ) : (
                      <code
                        className="block bg-muted p-2 rounded text-sm font-mono overflow-x-auto mb-2"
                        {...props}
                      />
                    ),
                  blockquote: ({ node, ...props }) => (
                    <blockquote
                      className="border-l-4 border-border pl-4 italic my-2 text-muted-foreground"
                      {...props}
                    />
                  ),
                  a: ({ node, ...props }) => (
                    <a
                      className="text-primary hover:underline"
                      target="_blank"
                      rel="noopener noreferrer"
                      {...props}
                    />
                  ),
                }}
              >
                {value}
              </ReactMarkdown>
            ) : (
              <p className="text-muted-foreground text-sm">Keine Vorschau verfügbar</p>
            )}
          </div>
        </TabsContent>
      </div>
    </Tabs>
  );
};
